import { fmtDate, fmtMoney } from "@/lib/bill-utils";

// Downloads an arbitrary base64 blob (used for payment receipts — images or PDFs).
export const downloadBase64File = (base64, mime, filename) => {
  if (!base64) return null;
  try {
    const byteString = atob(base64);
    const bytes = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      bytes[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: mime || "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return filename;
  } catch {
    return null;
  }
};

// Opens the user's mail client to send a payment confirmation (proof of payment)
// to the vendor's email on the bill — downloads the bank receipt image/PDF so
// the user can attach it.
export const composePaymentReceiptEmail = (bill, payment) => {
  const extFromMime = (m) => {
    if (!m) return "bin";
    if (m.includes("pdf")) return "pdf";
    if (m.includes("png")) return "png";
    if (m.includes("webp")) return "webp";
    return "jpg";
  };

  let attachmentName = null;
  if (payment?.receipt_image_base64) {
    const ext = extFromMime(payment.receipt_image_mime);
    const safe = (s) =>
      (s || "receipt")
        .toString()
        .replace(/[^a-z0-9-_]+/gi, "_")
        .replace(/^_+|_+$/g, "");
    attachmentName = `${safe(bill.company)}_${safe(bill.invoice_number)}_receipt.${ext}`;
    downloadBase64File(
      payment.receipt_image_base64,
      payment.receipt_image_mime,
      attachmentName,
    );
  }

  const lines = [];
  lines.push(`Hi ${bill.company || "team"},`);
  lines.push("");
  lines.push(
    "Please find confirmation of payment for the invoice below. ",
  );
  lines.push("");
  lines.push(`Invoice Number:     ${bill.invoice_number || "-"}`);
  lines.push(`Company:            ${bill.company || "-"}`);
  lines.push(`Amount Paid:        ${fmtMoney(payment.amount)}`);
  lines.push(`Payment Date:       ${fmtDate(payment.paid_at)}`);
  if (payment.method) lines.push(`Method:             ${payment.method}`);
  if (payment.bank_receipt_number)
    lines.push(`Bank Receipt #:     ${payment.bank_receipt_number}`);
  lines.push(`Remaining Balance:  ${fmtMoney(bill.remaining_balance)}`);
  lines.push("");
  if (attachmentName) {
    lines.push(
      `A copy of the bank receipt has been downloaded as "${attachmentName}". Please attach it to this email before sending.`,
    );
    lines.push("");
  }
  lines.push("Thank you — please reply if there are any issues.");
  lines.push("");
  lines.push("Kind regards,");
  lines.push("The Outdoor Blind Company");

  const subject = `Payment Confirmation — Invoice #${bill.invoice_number} — ${bill.company}`;
  const body = lines.join("\n");
  const to = bill.email ? encodeURIComponent(bill.email) : "";
  const url = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}${ccParam(bill.cc_emails)}`;
  window.location.href = url;
  return {
    attachmentName,
    recipient: bill.email || null,
    cc: parseEmailList(bill.cc_emails),
  };
};

// Always send reminders to this email.
export const REMINDER_TO = "craig@theoutdoorblindcompany.com";

// NAB online banking login for the "Pay Now" link
export const NAB_LOGIN_URL = "https://ib.nab.com.au/";

// Downloads the bill image to the user's device so they can attach it.
export const downloadBillImage = (bill) => {
  if (!bill?.image_base64) return null;
  try {
    const mime = bill.image_mime || "image/jpeg";
    const byteString = atob(bill.image_base64);
    const bytes = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      bytes[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: mime });
    const url = URL.createObjectURL(blob);
    const ext = mime.split("/")[1] || "jpg";
    const safe = (s) =>
      (s || "bill")
        .toString()
        .replace(/[^a-z0-9-_]+/gi, "_")
        .replace(/^_+|_+$/g, "");
    const filename = `${safe(bill.company)}_${safe(bill.invoice_number)}.${ext}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return filename;
  } catch {
    return null;
  }
};

const markPaidUrl = (billId) => `${window.location.origin}/pay/${billId}`;

// Parses a comma/semicolon/whitespace-separated string of emails into a clean list.
export const parseEmailList = (raw) => {
  if (!raw) return [];
  return raw
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0 && s.includes("@"));
};

const ccParam = (ccEmails) => {
  const list = parseEmailList(ccEmails);
  return list.length ? `&cc=${encodeURIComponent(list.join(","))}` : "";
};

// Returns the email pieces (subject, body, to, cc) without opening the mail client.
// Used for the "Copy Email" fallback button.
export const composeBillEmailMeta = (bill) => {
  const subjectPrefix =
    bill.status === "paid"
      ? "Receipt"
      : bill.total_paid > 0
        ? "Payment Reminder (Partial)"
        : "Invoice Reminder";
  const subject = `${subjectPrefix} — Invoice #${bill.invoice_number} — ${bill.company}`;
  const body = buildEmailBody(bill);
  return {
    to: REMINDER_TO,
    cc: parseEmailList(bill.cc_emails),
    subject,
    body,
  };
};

// Builds a concise email body per the requested format.
const buildEmailBody = (bill) => {
  const lines = [];
  lines.push("Hi Craig,");
  lines.push("");
  lines.push("Reminder for the following invoice:");
  lines.push("");
  lines.push(`Invoice Number:   ${bill.invoice_number || "-"}`);
  lines.push(`Company:          ${bill.company || "-"}`);
  lines.push(`Amount:           ${fmtMoney(bill.remaining_balance ?? bill.amount_due)}`);
  lines.push(`Date Due:         ${fmtDate(bill.due_date)}`);
  lines.push("");
  lines.push("Bank Details:");
  lines.push(`  Account Name:   ${bill.account_name || "-"}`);
  lines.push(`  Bank:           ${bill.bank_name || "-"}`);
  lines.push(`  BSB:            ${bill.bsb || "-"}`);
  lines.push(`  Account No.:    ${bill.account_number || "-"}`);
  lines.push(`  Reference:      ${bill.payment_reference || bill.invoice_number || "-"}`);
  if (bill.bpay_biller_code || bill.bpay_reference) {
    lines.push("");
    lines.push("BPAY:");
    lines.push(`  Biller Code:    ${bill.bpay_biller_code || "-"}`);
    lines.push(`  Reference:      ${bill.bpay_reference || "-"}`);
  }
  if (bill.payid) {
    lines.push("");
    lines.push(`PayID:            ${bill.payid}`);
  }
  if (bill.payment_url) {
    lines.push("");
    lines.push(`Pay online:       ${bill.payment_url}`);
  }
  if (bill.accepted_methods) {
    lines.push("");
    lines.push(`Other methods:    ${bill.accepted_methods}`);
  }
  lines.push("");
  lines.push(`Pay Now (NAB Internet Banking): ${NAB_LOGIN_URL}`);
  lines.push("");
  lines.push("Once paid, mark it off in the app:");
  lines.push(markPaidUrl(bill.id));
  lines.push("");
  lines.push("— Billy");
  return lines.join("\n");
};

// Opens the user's default mail client (e.g. Outlook) with a pre-filled message.
// Returns details so the UI can show a fallback if the client doesn't open.
export const composeBillEmail = (bill) => {
  const attachmentName = downloadBillImage(bill);
  const subjectPrefix =
    bill.status === "paid"
      ? "Receipt"
      : bill.total_paid > 0
        ? "Payment Reminder (Partial)"
        : "Invoice Reminder";
  const subject = `${subjectPrefix} — Invoice #${bill.invoice_number} — ${bill.company}`;
  const body = buildEmailBody(bill);
  const to = encodeURIComponent(REMINDER_TO);
  const cc = ccParam(bill.cc_emails);
  const url = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}${cc}`;

  // Let the image download start before we trigger mailto (avoid race condition)
  setTimeout(() => {
    try {
      // Use window.open first (doesn't navigate away from app). If the browser
      // refuses (popup-blocked), fall back to location.href.
      const win = window.open(url, "_self");
      if (!win) window.location.href = url;
    } catch {
      window.location.href = url;
    }
  }, 120);

  return {
    attachmentName,
    recipient: REMINDER_TO,
    cc: parseEmailList(bill.cc_emails),
    subject,
    body,
    mailtoUrl: url,
    to: REMINDER_TO,
  };
};
